/**
 * DocumentComparisonStatus - Animated step-by-step progress display for a
 * running document comparison workflow.
 *
 * Shows the current stage (Extracting, Hashing, Comparing, Building Results)
 * with a file-level progress counter and a pulsing animation on the active step.
 */

import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

import { cn } from '@/lib/utils';
import type { DocumentComparisonStatus as StatusType } from '@/services/api/documentComparisonService';

const STAGES: { key: StatusType; label: string }[] = [
  { key: 'extracting', label: 'Extracting zip' },
  { key: 'hashing', label: 'Analyzing files' },
  { key: 'comparing', label: 'Comparing hashes' },
  { key: 'building_results', label: 'Building results' },
];

const stageIndex = (status: StatusType): number => {
  const idx = STAGES.findIndex((s) => s.key === status);
  return idx === -1 ? -1 : idx;
};

interface DocumentComparisonStatusProps {
  status: StatusType;
  processedCount: number;
  totalExternalFiles: number;
  totalInternalFiles: number;
}

export const DocumentComparisonStatusView: React.FC<
  DocumentComparisonStatusProps
> = ({ status, processedCount, totalExternalFiles }) => {
  const activeIdx = stageIndex(status);
  const totalFiles = totalExternalFiles || 0;

  return (
    <div className="space-y-4">
      {/* Stage steps */}
      <div className="space-y-1.5">
        {STAGES.map((stage, idx) => {
          const isActive = idx === activeIdx;
          const isDone = idx < activeIdx;

          return (
            <div
              key={stage.key}
              className={cn(
                'flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-all duration-300',
                isActive && 'bg-indigo-50/70 text-indigo-700 font-medium',
                isDone && 'text-gray-400',
                !isActive && !isDone && 'text-gray-300',
              )}
            >
              {isDone && (
                <CheckCircleIcon className="h-4 w-4 text-green-500 shrink-0" />
              )}
              {isActive && (
                <ArrowPathIcon className="h-4 w-4 animate-spin text-indigo-500 shrink-0" />
              )}
              {!isDone && !isActive && (
                <div className="h-4 w-4 rounded-full border-2 border-gray-200 shrink-0" />
              )}
              <span>{stage.label}</span>
            </div>
          );
        })}
      </div>

      {/* File progress */}
      {totalFiles > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              {processedCount} / {totalFiles} files processed
            </span>
            <span>
              {totalFiles > 0
                ? Math.round((processedCount / totalFiles) * 100)
                : 0}
              %
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-500 ease-out"
              style={{
                width: `${totalFiles > 0 ? (processedCount / totalFiles) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
